import { useIsLocalMode } from '../../lib/offline/state';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import {
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@mlm/ui';
import { searchMembers, searchStockists, type MemberSearchResult } from '../../lib/api';

/**
 * The customer step: who the sale is for. Today that is a search by referral
 * code, email, phone or name (a slot code matches too and binds that slot).
 * This is also where the member QR scan and the Walk-in button will sit.
 */
export function CustomerSheet({
  open,
  stockistMode,
  onPick,
  onClose,
}: {
  open: boolean;
  /** "Sell to Stockist": only members who own a stockist branch are offered. */
  stockistMode: boolean;
  onPick: (m: MemberSearchResult) => void;
  onClose: () => void;
}) {
  const local = useIsLocalMode();
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-[500px] gap-5 p-[25px] sm:rounded-[15px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">
            {stockistMode ? 'Select stockist' : 'Select customer'}
          </DialogTitle>
          <DialogDescription className="text-base">
            {stockistMode
              ? 'Search a member who owns a stockist branch. The items you ring up transfer into their stock.'
              : local
                ? 'Search by name, slot code or referral code.'
                : 'Search by referral code, slot code, email, phone or name.'}
          </DialogDescription>
        </DialogHeader>
        {/* Mounted only while open, so the box is empty every time it opens. */}
        {open && <CustomerSearch stockistMode={stockistMode} onPick={onPick} />}
      </DialogContent>
    </Dialog>
  );
}

function CustomerSearch({
  stockistMode,
  onPick,
}: {
  stockistMode: boolean;
  onPick: (m: MemberSearchResult) => void;
}) {
  const [q, setQ] = useState('');
  const local = useIsLocalMode();
  const trimmed = q.trim();
  const search = useQuery({
    queryKey: [stockistMode ? 'stockists-search' : 'members-search', trimmed],
    queryFn: () => (stockistMode ? searchStockists(trimmed) : searchMembers(trimmed)),
    enabled: trimmed.length >= 2,
    staleTime: 5_000,
  });
  const results = search.data ?? [];

  return (
    <div className="space-y-3">
      <label className="flex h-[54px] items-center gap-3 rounded-[15px] bg-muted px-[19px]">
        <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            // One match and Enter is unambiguous, e.g. a typed slot code.
            if (e.key === 'Enter' && results.length === 1) onPick(results[0]!);
          }}
          placeholder={
            stockistMode
              ? 'Stockist name / email / phone / branch'
              : local
                ? 'Name / slot code / referral code'
                : 'Referral code / email / phone / name'
          }
          aria-label={stockistMode ? 'Search stockists' : 'Search customers'}
          className="h-full w-full min-w-0 bg-transparent text-base outline-none placeholder:text-muted-foreground"
        />
      </label>

      {trimmed.length < 2 ? (
        <p className="px-1 text-base text-muted-foreground">Type at least 2 characters.</p>
      ) : search.isLoading ? (
        <p className="px-1 text-base text-muted-foreground">Searching…</p>
      ) : results.length === 0 ? (
        <p className="px-1 text-base text-muted-foreground">No matches.</p>
      ) : (
        <ul className="max-h-[50vh] divide-y overflow-y-auto rounded-[10px] border">
          {results.map((m, i) => (
            <li key={`${m.matchType}-${m.id}-${m.slotId ?? i}`}>
              <button
                type="button"
                onClick={() => onPick(m)}
                className="flex min-h-[60px] w-full flex-col justify-center px-4 py-2 text-left active:bg-muted"
              >
                <span className="flex items-center gap-2">
                  <span className="text-base font-medium">{m.fullName ?? m.email}</span>
                  {m.matchType === 'slot' && m.slotCode && (
                    <Badge variant="default" className="text-xs">
                      slot {m.slotCode}
                    </Badge>
                  )}
                </span>
                <span className="text-sm text-muted-foreground">
                  {/* Offline the device holds no email, on purpose. */}
                  {[m.email, m.referralCode ? `ref ${m.referralCode}` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
